import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, FileText, XCircle, ArrowLeft, Building2, User, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PublicContractView - Public page for viewing shared contracts
 * Accessed via /contract/:token (no auth required)
 */
export default function PublicContractView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API}/contracts/public/${token}`);
      setData(res.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        setError("This contract was not found.");
      } else if (status === 410) {
        setError("This contract link has expired.");
      } else {
        setError("Unable to load contract. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-indigo-400">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-indigo-500 mb-2">Contract Unavailable</h1>
          <p className="text-indigo-400">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 text-indigo-400 hover:text-indigo-400"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to ClientKit
          </Link>
        </div>
      </div>
    );
  }

  const { contract, project, client } = data || {};
  const clientDisplay = client?.company || client?.name || "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 lg:p-8" data-testid="public-contract-view">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Contract Agreement</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-indigo-500">{project?.title || "Contract"}</h1>
            </div>
          </div>
          
          {/* Meta info */}
          <div className="flex flex-wrap gap-4 text-sm text-indigo-400">
            {clientDisplay && (
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {clientDisplay}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              Version {contract?.version || 1}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              contract?.status === 'draft' ? 'bg-indigo-500/20 text-indigo-400' :
              contract?.status === 'sent' ? 'bg-blue-500/20 text-indigo-400' :
              contract?.status === 'signed' ? 'bg-green-500/20 text-green-400' :
              'bg-slate-500/20 text-indigo-400'
            }`}>
              {contract?.status?.charAt(0).toUpperCase() + contract?.status?.slice(1) || 'Draft'}
            </span>
          </div>
        </div>

        {/* Contract Content */}
        <div className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 border border-slate-700/50">
          <article className="prose pamber-invert pamber-slate max-w-none 
            pamber-headings:text-indigo-500 pamber-headings:font-normal
            pamber-h1:text-2xl pamber-h1:border-b pamber-h1:border-indigo-500/30 pamber-h1:pb-3
            pamber-h2:text-lg pamber-h2:text-slate-600 pamber-h2:mt-8
            pamber-p:text-slate-400 pamber-p:leading-relaxed
            pamber-li:text-slate-400
            pamber-strong:text-indigo-500 pamber-strong:font-semibold
            pamber-a:text-indigo-400">
            <ReactMarkdown>{contract?.md_body || ""}</ReactMarkdown>
          </article>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-400">
            Powered by <Link to="/" className="text-indigo-400 hover:text-indigo-400">ClientKit</Link>
          </p>
          <p className="text-xs text-slate-600 mt-2">
            This is a read-only preview. Contact the sender to discuss or sign this contract.
          </p>
        </div>
      </div>
    </div>
  );
}
